/*
 * LUNA London — Shared site behaviour: navigation, reveal-on-scroll,
 * toasts, scroll-to-top. Loaded on every page after cart.js.
 */
(function () {
  "use strict";

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------------- Sticky header shadow ---------------- */
  const header = document.querySelector(".site-header");
  if (header) {
    const onScroll = () => header.classList.toggle("is-scrolled", window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ---------------- Mobile menu ---------------- */
  const hamburger = document.querySelector("[data-hamburger]");
  const mobileMenu = document.querySelector("[data-mobile-menu]");
  const mobileScrim = document.querySelector("[data-mobile-scrim]");

  function setMobileMenu(open) {
    if (!hamburger || !mobileMenu) return;
    hamburger.setAttribute("aria-expanded", String(open));
    mobileMenu.setAttribute("data-open", String(open));
    if (mobileScrim) mobileScrim.setAttribute("data-open", String(open));
    document.body.style.overflow = open ? "hidden" : "";
    if (open) {
      const firstLink = mobileMenu.querySelector("a, button");
      if (firstLink) firstLink.focus();
    } else {
      hamburger.focus();
    }
  }

  if (hamburger) {
    hamburger.addEventListener("click", () => {
      const isOpen = hamburger.getAttribute("aria-expanded") === "true";
      setMobileMenu(!isOpen);
    });
  }
  if (mobileScrim) mobileScrim.addEventListener("click", () => setMobileMenu(false));
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && hamburger && hamburger.getAttribute("aria-expanded") === "true") {
      setMobileMenu(false);
    }
  });

  /* ---------------- Reveal on scroll ---------------- */
  const revealEls = document.querySelectorAll(".reveal");
  if (revealEls.length) {
    if (prefersReducedMotion || !("IntersectionObserver" in window)) {
      revealEls.forEach((el) => el.classList.add("is-visible"));
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
      );
      revealEls.forEach((el, i) => {
        el.style.transitionDelay = prefersReducedMotion ? "0ms" : `${Math.min(i % 6, 5) * 60}ms`;
        io.observe(el);
      });
    }
  }

  /* ---------------- Scroll-to-top ---------------- */
  const scrollTopBtn = document.querySelector("[data-scroll-top]");
  if (scrollTopBtn) {
    window.addEventListener(
      "scroll",
      () => scrollTopBtn.classList.toggle("is-visible", window.scrollY > 700),
      { passive: true }
    );
    scrollTopBtn.addEventListener("click", () => {
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
    });
  }

  /* ---------------- Toasts ---------------- */
  function ensureToastRegion() {
    let region = document.querySelector(".toast-region");
    if (!region) {
      region = document.createElement("div");
      region.className = "toast-region";
      region.setAttribute("aria-live", "polite");
      region.setAttribute("role", "status");
      document.body.appendChild(region);
    }
    return region;
  }

  function showToast(message, opts = {}) {
    const region = ensureToastRegion();
    const toast = document.createElement("div");
    toast.className = "toast";
    toast.innerHTML = `
      <span class="toast__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><path d="M4 12l5 5L20 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
      </span>
      <span>${message}</span>
    `;
    region.appendChild(toast);
    const life = opts.duration || 3200;
    setTimeout(() => {
      toast.classList.add("is-leaving");
      toast.addEventListener("animationend", () => toast.remove(), { once: true });
    }, life);
  }
  window.LunaToast = { show: showToast };

  /* ---------------- Generic focus-trap helper for drawers/modals ---------------- */
  function trapFocus(container) {
    const focusables = container.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    container.addEventListener("keydown", (e) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }
  window.LunaFocusTrap = trapFocus;

  /* ---------------- Accordéon générique (FAQ, PDP, Support) ---------------- */
  document.addEventListener("click", (e) => {
    const trigger = e.target.closest(".accordion-trigger");
    if (!trigger) return;
    const expanded = trigger.getAttribute("aria-expanded") === "true";
    const panel = document.getElementById(trigger.getAttribute("aria-controls"));
    trigger.setAttribute("aria-expanded", String(!expanded));
    if (panel) panel.classList.toggle("is-open", !expanded);
  });

  /* ---------------- Note agrégée PDP -> ouvre/scrolle vers l'accordéon Avis ---------------- */
  document.querySelectorAll(".pdp-rating").forEach((link) => {
    link.addEventListener("click", () => {
      const target = document.getElementById("pdp-reviews");
      if (!target) return;
      const trigger = target.querySelector(".accordion-trigger");
      if (trigger && trigger.getAttribute("aria-expanded") !== "true") trigger.click();
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  });

  /* ---------------- Wishlist (localStorage) ---------------- */
  const WISHLIST_KEY = "luna_wishlist";
  function getWishlist() { try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; } catch (e) { return []; } }
  function setWishlist(list) { localStorage.setItem(WISHLIST_KEY, JSON.stringify(list)); }
  function syncWishlistButtons() {
    const list = getWishlist();
    document.querySelectorAll(".product-card__wishlist[data-wishlist]").forEach((btn) => {
      btn.setAttribute("aria-pressed", String(list.includes(btn.getAttribute("data-wishlist"))));
    });
  }
  document.addEventListener("click", (e) => {
    const btn = e.target.closest(".product-card__wishlist[data-wishlist]");
    if (!btn) return;
    e.preventDefault();
    const handle = btn.getAttribute("data-wishlist");
    const list = getWishlist();
    const idx = list.indexOf(handle);
    if (idx === -1) { list.push(handle); btn.setAttribute("aria-pressed", "true"); showToast("Ajouté aux favoris"); }
    else { list.splice(idx, 1); btn.setAttribute("aria-pressed", "false"); showToast("Retiré des favoris"); }
    setWishlist(list);
  });
  syncWishlistButtons();

  /* ---------------- Barre d'achat sticky ---------------- */
  const stickyCta = document.querySelector("[data-sticky-cta]");
  const purchaseRow = document.querySelector(".purchase-row");
  if (stickyCta && purchaseRow && "IntersectionObserver" in window) {
    const obs = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      stickyCta.setAttribute("data-visible", String(show));
      stickyCta.setAttribute("aria-hidden", String(!show));
    }, { threshold: 0 });
    obs.observe(purchaseRow);
  }

  /* ---------------- Ajout au panier AJAX ---------------- */
  document.querySelectorAll('form[action$="/cart/add"], form[action*="/cart/add"]').forEach((form) => {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const submitBtn = e.submitter || form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.classList.add("btn--loading");
      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded", "Accept": "application/json" },
          body: new URLSearchParams(new FormData(form)),
        });
        if (!res.ok) throw new Error("add failed");
        const cart = await (await fetch("/cart.js", { headers: { "Accept": "application/json" } })).json();
        document.querySelectorAll("[data-cart-count]").forEach((el) => {
          el.textContent = cart.item_count;
          el.setAttribute("data-count", cart.item_count);
          const bumpTarget = el.closest(".icon-btn") || el;
          bumpTarget.classList.remove("icon-btn--bump");
          void bumpTarget.offsetWidth;
          bumpTarget.classList.add("icon-btn--bump");
        });
        if (window.LunaToast) window.LunaToast.show("Ajouté au panier");
      } catch (err) {
        window.location.href = "/cart"; /* repli comportement natif */
      } finally {
        if (submitBtn) submitBtn.classList.remove("btn--loading");
      }
    });
  });
})();
